import { useCallback, useState, type FormEvent } from "react";
import { Check, Loader2 } from "lucide-react";

import { formatDimensionValue } from "@/lib/formatDimensionValue";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import { Input } from "@/components/ui/input";
import { api } from "@/services/api";
import {
  buildClarificationSummaryItems,
  isFieldRequired,
  isFieldVisible,
} from "./clarificationFieldUtils";
import { KaiSuggestInput, type SuggestOption } from "./KaiSuggestInput";
import type { ClarificationField, PendingClarification } from "./kaiTypes";

const REQUIRED_ERROR = "This field is required";

function FieldLabel({
  label,
  required,
}: {
  label: string;
  required: boolean;
}) {
  return (
    <div className="mb-1.5 text-sm font-medium text-foreground">
      {label}
      {required && (
        <span className="ml-0.5 text-destructive" aria-hidden>
          *
        </span>
      )}
    </div>
  );
}

function FieldError({ show }: { show: boolean }) {
  if (!show) return null;
  return (
    <p className="mt-1 text-xs text-destructive" role="alert">
      {REQUIRED_ERROR}
    </p>
  );
}

function RadioTagField({
  field,
  value,
  onChange,
  disabled,
  required,
  invalid,
}: {
  field: ClarificationField;
  value: string;
  onChange: (v: string) => void;
  disabled: boolean;
  required: boolean;
  invalid: boolean;
}) {
  return (
    <div>
      <FieldLabel label={field.label} required={required} />
      <div
        role="radiogroup"
        aria-label={field.label}
        aria-invalid={invalid}
        className="flex flex-wrap gap-1.5"
      >
        {(field.options ?? []).map((opt) => {
          const selected = value === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              role="radio"
              aria-checked={selected}
              disabled={disabled}
              onClick={() => onChange(opt.value)}
              className={cn(
                "rounded-full border px-3 py-1 text-xs transition-colors",
                selected
                  ? "border-primary/40 bg-primary/15 text-foreground"
                  : "border-border/60 bg-foreground/[0.03] text-foreground/80 hover:bg-accent/40",
                invalid && !selected && "border-destructive/50",
                disabled && "opacity-60",
              )}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
      <FieldError show={invalid} />
    </div>
  );
}

function TextField({
  field,
  value,
  onChange,
  disabled,
  required,
  invalid,
}: {
  field: ClarificationField;
  value: string;
  onChange: (v: string) => void;
  disabled: boolean;
  required: boolean;
  invalid: boolean;
}) {
  return (
    <div>
      <FieldLabel label={field.label} required={required} />
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.placeholder ?? ""}
        disabled={disabled}
        aria-invalid={invalid}
        className={cn(
          "h-10 rounded-xl bg-foreground/[0.03]",
          invalid && "border-destructive focus-visible:ring-destructive",
        )}
      />
      <FieldError show={invalid} />
    </div>
  );
}

function DateField({
  field,
  value,
  onChange,
  disabled,
  required,
  invalid,
}: {
  field: ClarificationField;
  value: string;
  onChange: (v: string) => void;
  disabled: boolean;
  required: boolean;
  invalid: boolean;
}) {
  return (
    <div>
      <FieldLabel label={field.label} required={required} />
      <DatePicker
        value={value}
        onChange={onChange}
        placeholder={field.placeholder ?? "Select date"}
        disabled={disabled}
        allowClear={!required}
        className={cn(
          "h-10 rounded-xl bg-foreground/[0.03]",
          invalid && "border-destructive focus-visible:ring-destructive",
        )}
      />
      <FieldError show={invalid} />
    </div>
  );
}

function PortSelectField({
  field,
  value,
  onChange,
  disabled,
  required,
  invalid,
}: {
  field: ClarificationField;
  value: string;
  onChange: (v: string) => void;
  disabled: boolean;
  required: boolean;
  invalid: boolean;
}) {
  const loadOptions = useCallback(async (query: string): Promise<SuggestOption[]> => {
    if (query.trim().length < 2) return [];
    const ports = await api.searchPorts(query.trim());
    return ports.slice(0, 20).map((port) => ({
      value: port.locode,
      label: `${port.name} (${port.locode})`,
      hint: port.country ?? undefined,
    }));
  }, []);

  return (
    <div>
      <FieldLabel label={field.label} required={required} />
      <KaiSuggestInput
        value={value}
        onChange={onChange}
        loadOptions={loadOptions}
        placeholder={field.placeholder ?? "Search port name or LOCODE"}
        disabled={disabled}
        invalid={invalid}
      />
      <FieldError show={invalid} />
    </div>
  );
}

function BookingFilterSelectField({
  field,
  value,
  onChange,
  disabled,
  required,
  invalid,
}: {
  field: ClarificationField;
  value: string;
  onChange: (v: string) => void;
  disabled: boolean;
  required: boolean;
  invalid: boolean;
}) {
  const filterKey = field.filter_key ?? "sales_person";
  const loadOptions = useCallback(
    async (query: string): Promise<SuggestOption[]> => {
      const results = await api.getBookingFilterValues(
        filterKey,
        query.trim() || undefined,
        20,
      );
      return results.map((r) => ({
        value: r.value,
        label: formatDimensionValue(filterKey, r.label ?? r.value),
      }));
    },
    [filterKey],
  );

  return (
    <div>
      <FieldLabel label={field.label} required={required} />
      <KaiSuggestInput
        value={value}
        onChange={onChange}
        loadOptions={loadOptions}
        placeholder={field.placeholder ?? "Search…"}
        disabled={disabled}
        invalid={invalid}
      />
      <FieldError show={invalid} />
    </div>
  );
}

function KaiClarificationResolvedCard({
  clarification,
}: {
  clarification: PendingClarification;
}) {
  const summaryItems = buildClarificationSummaryItems(
    clarification.fields,
    clarification.answers ?? {},
    clarification.title,
  );

  return (
    <div
      className="w-full max-w-[min(100%,38rem)] rounded-xl border border-emerald-500/20 bg-emerald-500/[0.06] px-3 py-2.5"
      aria-label="Clarification answered"
    >
      <div className="flex items-start gap-2.5">
        <Check
          className="mt-0.5 size-4 shrink-0 text-emerald-600 dark:text-emerald-400"
          aria-hidden
        />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium leading-snug text-foreground">
            {clarification.title}
          </p>
          {summaryItems.length > 0 ? (
            <div className="mt-1.5 flex flex-col gap-1.5">
              {summaryItems.map((item, index) =>
                item.label ? (
                  <div key={`${item.label}-${index}`} className="text-xs">
                    <span className="text-muted-foreground">{item.label}</span>
                    <span className="mt-0.5 block font-medium text-foreground">
                      {item.value}
                    </span>
                  </div>
                ) : (
                  <span
                    key={`${item.value}-${index}`}
                    className="inline-flex w-fit max-w-full rounded-full border border-emerald-500/25 bg-emerald-500/10 px-2.5 py-0.5 text-xs font-medium text-foreground"
                  >
                    {item.value}
                  </span>
                ),
              )}
            </div>
          ) : (
            <p className="mt-0.5 text-xs text-muted-foreground">
              Answer submitted
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

interface KaiClarificationCardProps {
  clarification: PendingClarification;
  onSubmit: (id: string, answers: Record<string, string>) => void;
}

/** Inline Kai clarification form — glass card matching the v2 chat. */
export function KaiClarificationCard({
  clarification,
  onSubmit,
}: KaiClarificationCardProps) {
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [showValidation, setShowValidation] = useState(false);

  const isSubmitting = clarification.status === "submitting";
  const isResolved = clarification.status === "resolved";
  const disabled = isSubmitting || isResolved;
  // Last-resort net: the backend guard should stop empty forms from ever
  // reaching here, but if one slips through, submitting `{}` is useless — steer
  // the user to answer in the composer instead of a lone "Continue" button.
  const hasNoFields = !isResolved && clarification.fields.length === 0;

  const setAnswer = useCallback((fieldId: string, value: string) => {
    setAnswers((prev) => ({ ...prev, [fieldId]: value }));
  }, []);

  const isFieldMissing = useCallback(
    (field: ClarificationField) => {
      if (!isFieldVisible(field, answers)) return false;
      if (!isFieldRequired(field)) return false;
      return (answers[field.id] ?? "").trim().length === 0;
    },
    [answers],
  );

  const missingCount = clarification.fields.filter(isFieldMissing).length;

  function handleSubmit(event?: FormEvent) {
    event?.preventDefault();
    if (missingCount > 0) {
      setShowValidation(true);
      return;
    }
    onSubmit(clarification.clarificationId, answers);
  }

  if (isResolved) {
    return <KaiClarificationResolvedCard clarification={clarification} />;
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="glass-panel-strong clay-strong w-full max-w-[min(100%,38rem)] rounded-2xl px-4 py-3.5"
    >
      <p className="text-[11px] font-semibold tracking-wide text-muted-foreground uppercase">
        Kai needs a bit more
      </p>
      <h3 className="mt-0.5 text-sm font-semibold text-foreground">
        {clarification.title}
      </h3>
      {!hasNoFields && (
        <p className="mt-0.5 text-xs text-muted-foreground">
          Answer below to continue, or type a reply in the composer.
        </p>
      )}
      {hasNoFields && (
        <p className="mt-0.5 text-xs text-muted-foreground">
          Kai needs a detail but the form couldn&apos;t be built — type your
          answer in the composer below.
        </p>
      )}

      <div className="mt-3 space-y-3">
        {clarification.fields.map((field) => {
          if (!isFieldVisible(field, answers)) return null;
          const value = answers[field.id] ?? "";
          const required = isFieldRequired(field);
          const invalid = showValidation && isFieldMissing(field);
          return (
            <div key={field.id}>
              {field.type === "radio_tags" ? (
                <RadioTagField
                  field={field}
                  value={value}
                  onChange={(v) => setAnswer(field.id, v)}
                  disabled={disabled}
                  required={required}
                  invalid={invalid}
                />
              ) : field.type === "date" ? (
                <DateField
                  field={field}
                  value={value}
                  onChange={(v) => setAnswer(field.id, v)}
                  disabled={disabled}
                  required={required}
                  invalid={invalid}
                />
              ) : field.type === "port_select" ? (
                <PortSelectField
                  field={field}
                  value={value}
                  onChange={(v) => setAnswer(field.id, v)}
                  disabled={disabled}
                  required={required}
                  invalid={invalid}
                />
              ) : field.type === "booking_filter_select" ? (
                <BookingFilterSelectField
                  field={field}
                  value={value}
                  onChange={(v) => setAnswer(field.id, v)}
                  disabled={disabled}
                  required={required}
                  invalid={invalid}
                />
              ) : (
                <TextField
                  field={field}
                  value={value}
                  onChange={(v) => setAnswer(field.id, v)}
                  disabled={disabled}
                  required={required}
                  invalid={invalid}
                />
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {hasNoFields ? null : (
          <>
            <Button
              type="submit"
              className="h-8 rounded-xl px-3 text-xs"
              disabled={disabled}
            >
              {isSubmitting && <Loader2 className="size-3.5 animate-spin" />}
              Continue
            </Button>
            {showValidation && missingCount > 0 && (
              <p className="text-xs text-amber-600 dark:text-amber-400">
                {missingCount === 1
                  ? "1 required field is missing"
                  : `${missingCount} required fields are missing`}
              </p>
            )}
          </>
        )}
      </div>
    </form>
  );
}
